import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  IconButton,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Divider,
  Tooltip,
  Paper,
  Snackbar,
  Alert,
  CircularProgress,
  ButtonGroup,
  Button,
  Chip,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import DownloadIcon from '@mui/icons-material/Download';
import LockIcon from '@mui/icons-material/Lock';
import PaletteIcon from '@mui/icons-material/Palette';
// eslint-disable-next-line no-unused-vars
import PreviewIcon from '@mui/icons-material/Preview';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import ContentEditable from 'react-contenteditable';
import { getTemplates, switchResumeTemplate, downloadResumePDF, getDownloadStatus } from '../../services/api';
import { colorThemes, applyColorThemeToHtml } from '../../config/colorThemes';
import PricingModal from '../Billing/PricingModal';
import TemplateSelector from './TemplateSelector';
import TemplatePickerButton from './TemplatePickerButton';
import { mobilePreviewSelectSx, mobileZoomGroupSx, previewToolbarSx, previewToolbarSelectSx } from '../Common/formStyles';import { paginateHtml, extractPages, replacePageInHtml } from '../../utils/resumePagination';
import { BRAND } from '../../theme/tokens';

const A4_WIDTH_PX = 794;

const fonts = [
  { name: 'Inter', value: 'Inter, sans-serif' },
  { name: 'Roboto', value: 'Roboto, sans-serif' },
  { name: 'Poppins', value: 'Poppins, sans-serif' },
  { name: 'Arial', value: 'Arial, sans-serif' },
  { name: 'Times New Roman', value: 'Times New Roman, serif' },
  { name: 'Georgia', value: 'Georgia, serif' }
];

const ResumePreview = ({ resumeData, onContentChange, onTemplateSwitch, subscriptionStatus, triggerDownloadRef }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const showDownloadLabel = useMediaQuery(theme.breakpoints.up('lg'));
  const [zoom, setZoom] = useState(100);
  const [selectedFont, setSelectedFont] = useState('Inter, sans-serif');
  const [selectedColorTheme, setSelectedColorTheme] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [baseHtmlContent, setBaseHtmlContent] = useState('');
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [resumeContent, setResumeContent] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [switching, setSwitching] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  // Billing state
  const [pricingModalOpen, setPricingModalOpen] = useState(false);
  const [pricingReason, setPricingReason] = useState('not_paid');
  const [downloadStatus, setDownloadStatus] = useState(null); // { allowed, reason, downloadsSinceLastPayment }
  const [templateSelectorOpen, setTemplateSelectorOpen] = useState(false);
  const contentRef = useRef(null);
  const hasAutoFitRef = useRef(false);
  // Mirrors htmlContent for handlers that run between state updates (blur)
  const htmlContentRef = useRef('');

  // Fetch templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const data = await getTemplates();
        setTemplates(data);
      } catch (error) {
        console.error('Failed to fetch templates:', error);
      }
    };
    fetchTemplates();
  }, []);

  // Update content when resumeData changes: repaginate with real DOM
  // measurements (same algorithm the PDF generator runs) then display.
  useEffect(() => {
    let cancelled = false;
    if (resumeData?.html) {
      paginateHtml(resumeData.html).then(({ html }) => {
        if (cancelled) return;
        setBaseHtmlContent(html);
        setHtmlContent(html);
        htmlContentRef.current = html;
        displayPages(html);
      });
    }
    if (resumeData?.content) {
      setResumeContent(resumeData.content);
    }
    if (resumeData?.templateId) {
      setSelectedTemplateId(resumeData.templateId);
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeData]);

  // Fetch download permission status whenever resume changes
  useEffect(() => {
    if (!resumeData?.id) {
      setDownloadStatus(null);
      return;
    }
    getDownloadStatus(resumeData.id)
      .then(setDownloadStatus)
      .catch((err) => console.error('Failed to fetch download status:', err));
  }, [resumeData?.id, resumeData?.updatedAt]);

  useEffect(() => {
    hasAutoFitRef.current = false;
  }, [resumeData?.id]);

  useEffect(() => {
    if (!isMobile || pages.length === 0 || hasAutoFitRef.current) return;
    hasAutoFitRef.current = true;
    const available = window.innerWidth - 24;
    const fitZoom = Math.min(100, Math.max(50, Math.round((available / A4_WIDTH_PX) * 100 / 10) * 10));
    setZoom(fitZoom);
  }, [isMobile, pages.length]);

  // Expose handleDownload to parent via ref (used for post-checkout auto-download)
  useEffect(() => {
    if (triggerDownloadRef) {
      triggerDownloadRef.current = handleDownload;
    }
  }); // no dep array — always keep the ref pointing at the latest closure

  // Split a (paginated) document into per-page display fragments
  const displayPages = (html, { keepCurrentPage = false } = {}) => {
    if (!html) {
      setPages([]);
      setCurrentPage(0);
      return;
    }
    const pageArray = extractPages(html);
    setPages(pageArray);
    setCurrentPage((prev) =>
      keepCurrentPage ? Math.min(prev, pageArray.length - 1) : 0
    );
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 10, 150));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 10, 50));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, pages.length - 1));
  };

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 0));
  };

  const handleFontChange = (e) => {
    setSelectedFont(e.target.value);
  };

  const handleContentChange = (evt) => {
    const newContent = evt.target.value;

    // Update the current page fragment for display
    const updatedPages = [...pages];
    updatedPages[currentPage] = newContent;
    setPages(updatedPages);

    // Merge the edited page back into the full multi-page document so saving
    // never loses the other pages (read via ref: state lags during fast typing)
    const fullHtml = replacePageInHtml(htmlContentRef.current || htmlContent, currentPage, newContent);
    setHtmlContent(fullHtml);
    htmlContentRef.current = fullHtml;

    if (onContentChange) {
      onContentChange(fullHtml);
    }
  };

  // After the user finishes editing, repaginate: edits can grow or shrink
  // content, which changes where pages must break
  const handleEditBlur = async () => {
    const source = htmlContentRef.current || htmlContent;
    if (!source) return;
    try {
      const { html } = await paginateHtml(source);
      setHtmlContent(html);
      htmlContentRef.current = html;
      displayPages(html, { keepCurrentPage: true });
      if (onContentChange && html !== source) {
        onContentChange(html);
      }
    } catch (err) {
      console.error('Repagination after edit failed:', err);
    }
  };

  const handleDownload = async () => {
    if (!resumeData?.id) {
      setSnackbar({ open: true, message: 'Please generate a resume first', severity: 'warning' });
      return;
    }

    // Re-check download permission before making the PDF request
    let currentStatus = downloadStatus;
    if (!currentStatus) {
      try {
        currentStatus = await getDownloadStatus(resumeData.id);
        setDownloadStatus(currentStatus);
      } catch (err) {
        console.error('Failed to check download status:', err);
      }
    }

    // Gate: require payment if not allowed and no active subscription
    if (currentStatus && !currentStatus.allowed && !subscriptionStatus?.isActive) {
      setPricingReason(currentStatus.reason);
      setPricingModalOpen(true);
      return;
    }

    setDownloading(true);
    try {
      const remainingHeader = await downloadResumePDF(resumeData.id);

      // Refresh download status after a successful download
      getDownloadStatus(resumeData.id).then(setDownloadStatus).catch(() => {});

      const remaining = typeof remainingHeader === 'number' ? remainingHeader : null;
      const msg = remaining !== null && remaining >= 0
        ? `✅ PDF downloaded! ${remaining} download${remaining === 1 ? '' : 's'} remaining for this version.`
        : '✅ PDF downloaded successfully!';

      setSnackbar({ open: true, message: msg, severity: 'success' });
    } catch (error) {
      console.error('Failed to download PDF:', error);

      // Backend returned 402 — show payment modal
      const status = error.response?.status;
      const code = error.response?.data?.code;
      if (status === 402) {
        const reason = code === 'DOWNLOAD_LIMIT_REACHED' ? 'download_limit_reached' : 'not_paid';
        setPricingReason(reason);
        setPricingModalOpen(true);
        // Refresh status
        getDownloadStatus(resumeData.id).then(setDownloadStatus).catch(() => {});
      } else {
        setSnackbar({ open: true, message: 'Failed to generate PDF. Please try again.', severity: 'error' });
      }
    } finally {
      setDownloading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const applyTemplateSwitch = async (newTemplateId) => {
    if (!newTemplateId || newTemplateId === selectedTemplateId) return;

    if (!resumeData?.id) {
      setSnackbar({
        open: true,
        message: 'Please generate a resume first before switching templates',
        severity: 'warning',
      });
      return;
    }

    if (!resumeData?.html && !htmlContent) {
      setSnackbar({
        open: true,
        message: 'Resume is still being generated. Please wait...',
        severity: 'info',
      });
      return;
    }

    setSwitching(true);
    setSelectedTemplateId(newTemplateId);

    try {
      const switchedResume = await switchResumeTemplate(resumeData.id, newTemplateId);
      const { html: paginatedHtml } = await paginateHtml(switchedResume.html);
      setBaseHtmlContent(paginatedHtml);
      setHtmlContent(paginatedHtml);
      htmlContentRef.current = paginatedHtml;
      setSelectedColorTheme('');
      displayPages(paginatedHtml);

      if (onTemplateSwitch) {
        onTemplateSwitch(switchedResume);
      }

      const templateName = templates.find((t) => t.id === newTemplateId)?.name || newTemplateId;
      setSnackbar({
        open: true,
        message: `Template switched to ${templateName}`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Failed to switch template:', error);
      setSelectedTemplateId(resumeData.templateId || '');
      setSnackbar({
        open: true,
        message: 'Failed to switch template. Please try again.',
        severity: 'error',
      });
    } finally {
      setSwitching(false);
    }
  };

  const handleTemplateSelectFromGallery = async (templateId) => {
    setTemplateSelectorOpen(false);
    await applyTemplateSwitch(templateId);
  };

  const handleColorThemeChange = (e) => {
    const colorThemeId = e.target.value;
    
    if (!baseHtmlContent) {
      setSnackbar({
        open: true,
        message: 'Please generate a resume first',
        severity: 'warning'
      });
      return;
    }
    
    setSelectedColorTheme(colorThemeId);
    
    if (colorThemeId === '') {
      // Reset to original colors (baseHtmlContent is already paginated)
      setHtmlContent(baseHtmlContent);
      htmlContentRef.current = baseHtmlContent;
      displayPages(baseHtmlContent, { keepCurrentPage: true });
      setSnackbar({
        open: true,
        message: 'Color theme reset to original',
        severity: 'info'
      });
      return;
    }
    
    // Find the selected color theme
    const colorTheme = colorThemes.find(theme => theme.id === colorThemeId);
    if (colorTheme) {
      // Colors don't affect layout, so no repagination is needed
      const themedHtml = applyColorThemeToHtml(baseHtmlContent, colorTheme);
      setHtmlContent(themedHtml);
      htmlContentRef.current = themedHtml;
      displayPages(themedHtml, { keepCurrentPage: true });
      
      setSnackbar({
        open: true,
        message: `🎨 Applied ${colorTheme.name} color theme!`,
        severity: 'success'
      });
    }
  };

  const downloadLocked = downloadStatus && !downloadStatus.allowed && !subscriptionStatus?.isActive;
  const downloadTooltip = downloading
    ? 'Generating PDF…'
    : downloadLocked
      ? 'Payment required to download'
      : 'Download as PDF';
  const downloadsRemaining =
    downloadStatus?.allowed &&
    downloadStatus.reason === 'within_limit' &&
    !subscriptionStatus?.isActive
      ? 5 - (downloadStatus.downloadsSinceLastPayment || 0)
      : null;
  const selectedTemplateName =
    templates.find((t) => t.id === selectedTemplateId)?.name ||
    (selectedTemplateId
      ? selectedTemplateId.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
      : 'Template');
  const selectedColorName =
    colorThemes.find((t) => t.id === selectedColorTheme)?.name || 'Colors';
  const selectedFontName = fonts.find((f) => f.value === selectedFont)?.name || 'Font';

  const renderTemplatePicker = (compact = false) =>
    compact ? (
      <Tooltip title={switching ? 'Switching template…' : 'Change template'}>
        <span>
          <Button
            size="small"
            onClick={() => setTemplateSelectorOpen(true)}
            disabled={switching || !htmlContent}
            startIcon={<PaletteIcon fontSize="small" />}
            sx={mobilePreviewSelectSx}
          >
            {switching ? '…' : selectedTemplateName}
          </Button>
        </span>
      </Tooltip>
    ) : (
      <Box sx={{ minWidth: 200, maxWidth: 280, flexShrink: 0 }}>
        <TemplatePickerButton
          templateId={selectedTemplateId}
          templates={templates}
          onClick={() => setTemplateSelectorOpen(true)}
          disabled={switching || !htmlContent}
          compact
          label="Layout"
        />
      </Box>
    );

  const renderColorSelect = (compact = false) => (
    <Tooltip title="Color theme">
      <FormControl size="small" sx={{ minWidth: compact ? 'auto' : { xs: 120, sm: 140 }, flexShrink: 0 }}>
        <Select
          value={selectedColorTheme}
          onChange={handleColorThemeChange}
          disabled={!htmlContent}
          displayEmpty
          sx={compact ? mobilePreviewSelectSx : previewToolbarSelectSx}
          renderValue={() => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, overflow: 'hidden' }}>
              {selectedColorTheme ? (
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    flexShrink: 0,
                    bgcolor: colorThemes.find((t) => t.id === selectedColorTheme)?.colors.primary,
                  }}
                />
              ) : (
                <ColorLensIcon sx={{ fontSize: 16, flexShrink: 0, opacity: 0.7 }} />
              )}
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {selectedColorName}
              </Box>
            </Box>
          )}
        >
          <MenuItem value="">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ColorLensIcon fontSize="small" />
              <em>Colors</em>
            </Box>
          </MenuItem>
          {colorThemes.map((colorTheme) => (
            <MenuItem key={colorTheme.id} value={colorTheme.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: colorTheme.colors.primary,
                  }}
                />
                {colorTheme.name}
                {colorTheme.id === selectedColorTheme && ' ✓'}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Tooltip>
  );

  const renderFontSelect = (compact = false) => (
    <FormControl size="small" sx={{ minWidth: compact ? 'auto' : { xs: 100, sm: 110 }, flexShrink: 0 }}>
      <Select
        value={selectedFont}
        onChange={handleFontChange}
        displayEmpty
        sx={compact ? mobilePreviewSelectSx : previewToolbarSelectSx}
        renderValue={() => (
          <Box
            component="span"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontFamily: selectedFont,
            }}
          >
            {selectedFontName}
          </Box>
        )}
      >
        {fonts.map((font) => (
          <MenuItem key={font.value} value={font.value} style={{ fontFamily: font.value }}>
            {font.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const renderPageNav = () =>
    pages.length > 1 ? (
      <Box sx={mobileZoomGroupSx}>
        <IconButton size="small" onClick={handlePrevPage} disabled={currentPage === 0} sx={{ borderRadius: 0 }}>
          <NavigateBeforeIcon fontSize="small" />
        </IconButton>
        <Typography
          variant="caption"
          sx={{ minWidth: 44, textAlign: 'center', color: 'text.secondary', fontWeight: 600, px: 0.5 }}
        >
          {currentPage + 1}/{pages.length}
        </Typography>
        <IconButton
          size="small"
          onClick={handleNextPage}
          disabled={currentPage === pages.length - 1}
          sx={{ borderRadius: 0 }}
        >
          <NavigateNextIcon fontSize="small" />
        </IconButton>
      </Box>
    ) : null;

  const renderZoomControls = (compact = false) => (
    <Box sx={compact ? mobileZoomGroupSx : { display: 'inline-flex', alignItems: 'center' }}>
      <Tooltip title="Zoom out">
        <span>
          <IconButton size="small" onClick={handleZoomOut} disabled={zoom === 50 || !htmlContent}>
            <ZoomOutIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Typography
        variant="caption"
        sx={{
          minWidth: compact ? 40 : 36,
          textAlign: 'center',
          color: 'text.secondary',
          fontWeight: 600,
        }}
      >
        {zoom}%
      </Typography>
      <Tooltip title="Zoom in">
        <span>
          <IconButton size="small" onClick={handleZoomIn} disabled={zoom === 150 || !htmlContent}>
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );

  const renderDownloadButton = (mobile = false) => (
    <Tooltip title={downloadTooltip}>
      <span>
        <Button
          variant="contained"
          size="small"
          onClick={handleDownload}
          color={downloadLocked ? 'warning' : 'primary'}
          disabled={downloading || !htmlContent}
          startIcon={
            downloading ? (
              <CircularProgress size={16} color="inherit" />
            ) : downloadLocked ? (
              <LockIcon fontSize="small" />
            ) : (
              <DownloadIcon fontSize="small" />
            )
          }
          sx={{
            flexShrink: 0,
            minWidth: mobile ? 96 : showDownloadLabel ? 108 : 40,
            height: mobile ? 40 : undefined,
            px: mobile ? 1.75 : showDownloadLabel ? 1.5 : 1,
            borderRadius: 2,
            fontWeight: 700,
            fontSize: mobile ? '0.8125rem' : undefined,
            '& .MuiButton-startIcon': { mr: mobile || showDownloadLabel ? 0.75 : 0 },
          }}
        >
          {mobile ? (downloadLocked ? 'Unlock' : 'PDF') : showDownloadLabel ? 'Download' : null}
        </Button>
      </span>
    </Tooltip>
  );

  const renderDesktopToolbar = () => (
    <Box sx={previewToolbarSx}>
      <Typography
        variant="caption"
        fontWeight={700}
        sx={{
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          flexShrink: 0,
          background: BRAND.gradientText,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}
      >
        Preview
      </Typography>

      <Divider orientation="vertical" flexItem sx={{ my: 0.5, flexShrink: 0 }} />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flex: 1,
          minWidth: 0,
          overflowX: 'auto',
          overflowY: 'hidden',
          py: 0.25,
          scrollbarWidth: 'thin',
        }}
      >
        {renderTemplatePicker()}
        {renderColorSelect()}
        {renderFontSelect()}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          flexShrink: 0,
          pl: 1,
          borderLeft: 1,
          borderColor: 'divider',
        }}
      >
        {pages.length > 1 && (
          <>
            <ButtonGroup size="small" variant="outlined">
              <IconButton size="small" onClick={handlePrevPage} disabled={currentPage === 0}>
                <NavigateBeforeIcon fontSize="small" />
              </IconButton>
              <Button disabled sx={{ minWidth: 56, px: 1, fontSize: '0.75rem' }}>
                {currentPage + 1}/{pages.length}
              </Button>
              <IconButton size="small" onClick={handleNextPage} disabled={currentPage === pages.length - 1}>
                <NavigateNextIcon fontSize="small" />
              </IconButton>
            </ButtonGroup>
            <Divider orientation="vertical" flexItem sx={{ my: 0.5, mx: 0.5 }} />
          </>
        )}

        {renderZoomControls()}
        <Divider orientation="vertical" flexItem sx={{ my: 0.5, mx: 0.5 }} />
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          {renderDownloadButton()}
          {downloadsRemaining !== null && (
            <Chip
              label={`${downloadsRemaining} left`}
              size="small"
              color="warning"
              sx={{
                position: 'absolute',
                top: -8,
                right: -6,
                height: 16,
                fontSize: '0.6rem',
                pointerEvents: 'none',
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );

  const renderMobileToolbar = () => (
    <Box sx={[previewToolbarSx, { flexDirection: 'column', alignItems: 'stretch', gap: 0.75 }]}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: { xs: 0, sm: 0 },
          py: 0.25,
        }}
      >
        {renderPageNav()}
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>{renderZoomControls(true)}</Box>
        {renderDownloadButton(true)}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pb: 0.5,
          overflowX: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {renderTemplatePicker(true)}
        {renderColorSelect(true)}
        {renderFontSelect(true)}
      </Box>

      {downloadsRemaining !== null && (
        <Typography
          variant="caption"
          color="warning.main"
          sx={{ display: 'block', px: 2, pb: 1, mt: -0.5, fontWeight: 600 }}
        >
          {downloadsRemaining} PDF download{downloadsRemaining === 1 ? '' : 's'} remaining
        </Typography>
      )}
    </Box>
  );

  return (
    <>
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      {isMobile ? renderMobileToolbar() : renderDesktopToolbar()}

      {/* Preview Area */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          bgcolor: (t) =>
            t.palette.mode === 'dark'
              ? 'rgba(0,0,0,0.35)'
              : 'rgba(15, 23, 42, 0.06)',
          backgroundImage: (t) =>
            t.palette.mode === 'dark'
              ? 'radial-gradient(circle at 50% 0%, rgba(99,102,241,0.08) 0%, transparent 50%)'
              : 'radial-gradient(circle at 50% 0%, rgba(99,102,241,0.06) 0%, transparent 55%)',
          px: isMobile ? 1.5 : 3,
          pb: isMobile ? 2 : 3,
          pt: isMobile ? 1.5 : 2,
        }}
      >
        {pages.length > 0 ? (
          <Box
            sx={{
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s',
              width: '210mm',
            }}
          >
            <Box
              sx={{
                fontFamily: selectedFont,
                '& .resume-page': {
                  width: '100%',
                  minHeight: '297mm',
                  backgroundColor: 'white',
                  boxShadow: (t) =>
                    t.palette.mode === 'dark'
                      ? '0 8px 32px rgba(0,0,0,0.5)'
                      : '0 12px 40px rgba(15, 23, 42, 0.18)',
                  boxSizing: 'border-box',
                  borderRadius: '2px',
                },
              }}
            >
              <ContentEditable
                key={`page-${currentPage}`}
                innerRef={contentRef}
                html={pages[currentPage] || ''}
                disabled={false}
                onChange={handleContentChange}
                onBlur={handleEditBlur}
                style={{
                  outline: 'none',
                  fontFamily: selectedFont,
                  cursor: 'text'
                }}
              />
            </Box>
          </Box>
        ) : (
          <Paper
            elevation={4}
            sx={{
              width: '100%',
              maxWidth: '210mm',
              minHeight: '297mm',
              bgcolor: 'background.paper',
              p: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary'
            }}
          >
            <Typography variant="body1" align="center">
              Fill in the form and click "Generate Resume with AI"<br />
              to see your resume preview here
            </Typography>
          </Paper>
        )}
      </Box>
    </Box>

    {/* Snackbar for notifications */}
    <Snackbar
      open={snackbar.open}
      autoHideDuration={5000}
      onClose={handleSnackbarClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        onClose={handleSnackbarClose}
        severity={snackbar.severity || 'info'}
        variant="outlined"
        sx={{
          bgcolor: 'background.paper',
          borderColor: 'primary.main',
          color: 'text.primary',
          '& .MuiAlert-icon': { color: 'primary.main' },
        }}
      >
        {snackbar.message}
      </Alert>
    </Snackbar>

    {/* Payment Modal */}
    <PricingModal
      open={pricingModalOpen}
      onClose={() => setPricingModalOpen(false)}
      resumeId={resumeData?.id}
      reason={pricingReason}
    />

    <TemplateSelector
      open={templateSelectorOpen}
      onClose={() => setTemplateSelectorOpen(false)}
      onSelectTemplate={handleTemplateSelectFromGallery}
      selectedTemplateId={selectedTemplateId}
    />
    </>
  );
};

export default ResumePreview;

